import { SimpleGrid } from '@mantine/core';
import type { MonthRevenue, RevenueOverview } from 'src/secret-menu/billing/hooks/useRevenue';
import { describeChange } from '../overview/lenses';
import { previousMonth, type UsageModel } from '../overview/usageModel';
import { BilledUnitsStat, FullAdoptionStat, MrrStat } from './RevenueStats';
import { formatUnits, Stat, toneOf } from './ui';

type Props = {
  model: UsageModel;
  month: string;
  overview: RevenueOverview;
  history: readonly MonthRevenue[];
  today: Date;
};

/**
 * The four headline numbers for the all-hands. ABC follows the month picker; the three revenue
 * figures are today's and say so, and are the same components the Revenue tab shows.
 */
export default function KpiHeadline({ model, month, overview, history, today }: Props) {
  const prev = previousMonth(model, month);
  const usageNow = model.platform.get(month)?.abc ?? 0;
  const usageBefore = prev ? (model.platform.get(prev)?.abc ?? 0) : null;
  const excludingNew = model.platform.get(month)?.usage ?? 0;
  const monthLabel = model.monthLabels.get(month) ?? month;

  let usageDetail = `${monthLabel} · no earlier month`;
  let tone = toneOf(null);
  if (prev && usageBefore !== null) {
    const change = describeChange(usageNow, usageBefore);
    usageDetail = `${change.label} on ${model.monthLabels.get(prev) ?? prev} (${formatUnits(usageBefore)})`;
    if (change.direction === 'up' || change.direction === 'down')
      tone = toneOf(usageNow - usageBefore);
  }
  if (month === model.partialMonth) {
    usageDetail = `${monthLabel} so far · month in progress`;
    tone = 'neutral';
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
      <Stat
        label="Total actions by customers (ABC)"
        value={formatUnits(usageNow)}
        detail={`${usageDetail} · ${formatUnits(excludingNew)} excluding new clients`}
        tone={tone}
        hint="actionsByCustomers"
      />
      <BilledUnitsStat overview={overview} history={history} today={today} />
      <MrrStat overview={overview} history={history} today={today} />
      <FullAdoptionStat overview={overview} />
    </SimpleGrid>
  );
}
